import {
  Box,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  styled,
  useTheme
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BaseAPI } from 'src/providers/base-api';
import { NEAR_BLACK_070919_A18, NEAR_BLACK_070919_A15 } from 'src/theme/tokens/colors';

const LogoWrapper = styled(Link)(
  ({ theme }) => `
    color: ${theme.palette.text.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    width: 100%;
    margin: 0 auto;
    font-weight: ${theme.typography.fontWeightBold};
  `
);

const TooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.alpha.trueWhite[100],
    color: theme.palette.getContrastText(theme.colors.alpha.trueWhite[100]),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    borderRadius: theme.general.borderRadiusSm,
    boxShadow:
      `0 .2rem .8rem ${NEAR_BLACK_070919_A18}, 0 .08rem .15rem ${NEAR_BLACK_070919_A15}`
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.alpha.trueWhite[100]
  }
}));

function Logo() {
  const theme = useTheme();
  const [imgSrc, setImgSrc] = useState<string>('');
  const [organisation, setOrganisation] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getSettingslist = async () => {
    try {
      if (isMountedRef.current) setLoading(true);

      // Get tenant ID from JWT token
      const tenantId = BaseAPI.getTenantIdFromToken();
      if (!tenantId) {
        console.warn('Tenant ID not found in token');
        if (isMountedRef.current) setLoading(false);
        return;
      }

      await BaseAPI.getSettingsDetails(async (result: any) => {
        if (!isMountedRef.current) return;

        if (result.status === 200) {
          for (const dataValue of result.data) {
            if (dataValue.group === 'OrganisationProfile') {
              if (isMountedRef.current) setOrganisation(dataValue?.value);

              if (dataValue?.value?.logoAssetId) {
                // Use public logo endpoint with tenant ID to prevent cross-tenant caching
                // URL format: /public/t/{tenantId}/logo/{assetId}
                const logoUrl = BaseAPI.buildAPIURL(
                  `/public/t/${tenantId}/logo/${dataValue.value.logoAssetId}`
                );
                if (isMountedRef.current) {
                  setImgSrc(logoUrl);
                }
              }
              break;
            }
          }
        } else {
          console.error('Failed to fetch settings:', result.message);
        }
        if (isMountedRef.current) setLoading(false);
      });
    } catch (e) {
      console.error('Error in getSettingslist:', e);
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    getSettingslist();
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 0'
      }}
    >
      <TooltipWrapper title={organisation?.companyName || 'Company Logo'} arrow>
        <LogoWrapper to="/overview">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={organisation?.companyName || 'Company logo'}
              style={{
                width: 'auto',
                height: '50px',
                // objectFit: 'contain',
                // display: 'block'
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.secondary
              }}
            >
              {loading ? 'Loading...' : 'Logo'}
            </Box>
          )}
        </LogoWrapper>
      </TooltipWrapper>
    </Box>
  );
}

export default Logo;

// import {
//   Box,
//   Tooltip,
//   Badge,
//   TooltipProps,
//   tooltipClasses,
//   styled,
//   useTheme,
//   Avatar
// } from '@mui/material';
// import { useEffect, useState } from 'react';
// import { Link } from 'react-router-dom';
// import { BaseAPI } from 'src/providers/base-api';

// const LogoWrapper = styled(Link)(
//   ({ theme }) => `
//         color: ${theme.palette.text.primary};
//         display: flex;
//         text-decoration: none;
//         width: 53px;
//         margin: 0 auto;
//         font-weight: ${theme.typography.fontWeightBold};
// `
// );

// const LogoSignWrapper = styled(Box)(
//   () => `
//         width: 52px;
//         height: 38px;
// `
// );

// const LogoSign = styled(Box)(
//   ({ theme }) => `
//         background: ${theme.general.reactFrameworkColor};
//         width: 18px;
//         height: 18px;
//         border-radius: ${theme.general.borderRadiusSm};
//         position: relative;
//         transform: rotate(45deg);
//         top: 3px;
//         left: 17px;

//         &:after, 
//         &:before {
//             content: "";
//             display: block;
//             width: 18px;
//             height: 18px;
//             position: absolute;
//             top: -1px;
//             right: -20px;
//             transform: rotate(0deg);
//             border-radius: ${theme.general.borderRadiusSm};
//         }

//         &:before {
//             background: ${theme.palette.primary.main};
//             right: auto;
//             left: 0;
//             top: 20px;
//         }

//         &:after {
//             background: ${theme.palette.secondary.main};
//         }
// `
// );

// const LogoSignInner = styled(Box)(
//   ({ theme }) => `
//         width: 16px;
//         height: 16px;
//         position: absolute;
//         top: 12px;
//         left: 12px;
//         z-index: 5;
//         border-radius: ${theme.general.borderRadiusSm};
//         background: ${theme.header.background};
// `
// );

// const TooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
//   <Tooltip {...props} classes={{ popper: className }} />
// ))(({ theme }) => ({
//   [`& .${tooltipClasses.tooltip}`]: {
//     backgroundColor: theme.colors.alpha.trueWhite[100],
//     color: theme.palette.getContrastText(theme.colors.alpha.trueWhite[100]),
//     fontSize: theme.typography.pxToRem(12),
//     fontWeight: 'bold',
//     borderRadius: theme.general.borderRadiusSm,
//     boxShadow:
//       '0 .2rem .8rem rgba(7,9,25,.18), 0 .08rem .15rem rgba(7,9,25,.15)'
//   },
//   [`& .${tooltipClasses.arrow}`]: {
//     color: theme.colors.alpha.trueWhite[100]
//   }
// }));



// function Logo() {
//   const theme = useTheme();

  
  
//   const[imgSrc,setImgSrc]=useState<string>();
//   const [upDateId, setUpDateId] = useState<any>();
//   const [organisation, setOrganisation] = useState<any>();

//   // const [logoAssetId,setLogoAssetId]=useState() ;
//   // const getSettingslist = async () => {
//   //   try {
//   //     await BaseAPI.getSettingsDetails((result: any) => {
//   //       if (result.status === 200) {
//   //         const dateValue = result?.data?.length
//   //         const dateformatvalue = result?.data[0]?.value?.dateFormat
//   //         result.data.map(async (dataValue: any) => {
            
//   //           switch (dataValue.group) {
//   //             case 'OrganisationProfile': {
//   //               setUpDateId(dataValue?.id);
//   //               setOrganisation(dataValue?.value);
//   //               const res = await BaseAPI.getAssetSignedUrl(organisation?.logoAssetId);
//   //               setImgSrc(res?.url);
                
//   //               break;
//   //             }
//   //             default: {
//   //               "No  Value Added"
//   //               break;
//   //             }
//   //           }
//   //         })
          
//   //       } else {
//   //         console.error(result.message);
//   //       }
//   //     });
//   //   } catch (e) {
//   //     console.error(e);
//   //   }
//   // };

//   // useEffect(() => {
//   //   getSettingslist();
//   // },[])

//   const getSettingslist = async () => {
//     try {
//       const result = await BaseAPI.getSettingsDetails(async (result: any)=>{
//         if (result.status === 200) {
//           const dateValue = result?.data?.length;
//           const dateformatvalue = result?.data[0]?.value?.dateFormat;
    
//           for (const dataValue of result.data) {
//             switch (dataValue.group) {
//               case 'OrganisationProfile': {
//                 setUpDateId(dataValue?.id);
//                 setOrganisation(dataValue?.value);
  
//                 if (dataValue?.value?.logoAssetId) {
//                   const res = await BaseAPI.getAssetSignedUrl(dataValue.value.logoAssetId);
//                   setImgSrc(res?.url);
//                 }
//                 break;
//               }
//               default: {
//                 console.error("No Value Added");
//                 break;
//               }
//             }
//           }
//         } 
//       } 
//     )
      
//     } catch (e) {
//       console.error(e);
//     }
//   };
  
//   useEffect(() => {
//     getSettingslist();
//   }, []);
  
 
  
//   return (
//     <TooltipWrapper
//       title={organisation?.companyName}
//       arrow
//     >
//       <LogoWrapper to="/overview">
//         {
          
//           <img width='180px' height='45' src={imgSrc} />
            
//         }

//       </LogoWrapper>
//     </TooltipWrapper>
//   );
// }

// export default Logo;
